"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { ArrowDown, ArrowUpRight, Check } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/** Design size for the hero frame (px) */
const HERO_W = 668.4;
const HERO_H = 602;

export interface LifeMilestone2Data {
  id?: string;
  status?: boolean;
  background_color?: string;
  block_main_title?: string;
  block_subtitle?: string;
  block_body?: string;
  block_cta_label?: string;
  block_cta_url?: string;
  /** Open lead popup vs navigate to block_cta_url */
  is_cta?: boolean;
  block_feature_label?: string;
  hero_image?: string;
  hero_image_alt?: string;
}

interface LifeMilestone2Props {
  data: LifeMilestone2Data | { data: LifeMilestone2Data };
}

function resolvePayload(
  data: LifeMilestone2Props["data"],
): LifeMilestone2Data | undefined {
  const anyData = data as { data?: LifeMilestone2Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as LifeMilestone2Data;
  }
  return data as LifeMilestone2Data;
}

/** Next.js `Link`–safe path for internal CTAs */
function normalizeCtaHref(url: string): string {
  const h = url.trim();
  if (!h) return "";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

const LifeMilestone2 = ({ data }: LifeMilestone2Props) => {
  const dispatch = useDispatch();
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);

  const {
    status = true,
    background_color = "#291973",
    block_main_title = "",
    block_subtitle = "",
    block_body = "",
    block_cta_label = "",
    block_cta_url = "",
    block_feature_label = "",
    hero_image = "",
    hero_image_alt = "",
  } = sectionData || {};

  const rawSection = sectionData as Record<string, unknown> | undefined;
  const isCtaOn = isCmsShowFlagOn(rawSection?.is_cta ?? rawSection?.isCta, true);

  const trimmedCtaUrl = String(block_cta_url ?? "").trim();
  const ctaHrefForLink = trimmedCtaUrl
    ? normalizeCtaHref(trimmedCtaUrl)
    : "";
  const ctaOpensLeadPopup = isCtaOn || !ctaHrefForLink;
  const ctaIsExternal = /^https?:\/\//i.test(trimmedCtaUrl);

  const openLeadFromCta = useCallback(() => {
    ctaClick({
      section: "life_milestone_2",
      component: "block_cta",
      label: block_cta_label?.trim() || "cta",
      step: "1",
      entity: "life_milestone",
      parentEntity: "life_milestone_2",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  }, [block_cta_label, dispatch]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current && status) {
  //     FireWidgetViewedEvent(ref, "life_milestone_2");
  //   }
  // }, [status]);

  if (!status) return null;

  const hasCopy =
    block_main_title ||
    block_subtitle ||
    block_body ||
    block_cta_label ||
    block_feature_label;

  if (!hasCopy) return null;

  const ctaButtonClass =
    "inline-flex items-center gap-2 rounded-md border border-white bg-transparent px-5 py-2.5 text-[16px] font-medium text-white transition-colors hover:bg-white/10";

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "w-full px-6 py-10 min-[640px]:px-8 min-[768px]:px-11 min-[768px]:py-[52px]",
      )}
      style={{
        backgroundColor: background_color,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <div className="mx-auto flex max-w-[1200px] flex-col gap-10 min-[1024px]:flex-row min-[1024px]:items-center min-[1024px]:justify-between min-[1024px]:gap-10 min-[1280px]:gap-14">
        <div className="flex min-w-0 flex-1 flex-col gap-4 text-center text-white min-[1024px]:max-w-[540px] min-[1024px]:text-left">
          {block_main_title ? (
            <h2 className="text-[32px] font-bold leading-[2] tracking-tight text-white min-[1024px]:text-[52px] min-[1024px]:leading-[1.12]">
              {block_main_title}
            </h2>
          ) : null}
          {block_subtitle ? (
            <p className="text-[16px] font-bold leading-tight text-white min-[1024px]:text-[32px]">
              {block_subtitle}
            </p>
          ) : null}
          {block_body ? (
            <p className="text-[16px] font-normal leading-relaxed text-white/95 min-[640px]:text-[17px] min-[1024px]:text-[18px] min-[1024px]:leading-[1.55]">
              {block_body}
            </p>
          ) : null}
          {block_cta_label ? (
            <div className="order-1 flex justify-center pt-1 min-[1024px]:order-none min-[1024px]:justify-start">
              {ctaOpensLeadPopup ? (
                <button
                  type="button"
                  className={ctaButtonClass}
                  onClick={openLeadFromCta}
                >
                  {block_cta_label}
                  <ArrowDown className="size-4 shrink-0 min-[1024px]:hidden" aria-hidden />
                  <ArrowUpRight
                    className="hidden size-4 shrink-0 min-[1024px]:block"
                    aria-hidden
                  />
                </button>
              ) : ctaIsExternal ? (
                <a
                  href={trimmedCtaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={ctaButtonClass}
                  onClick={() =>
                    ctaClick({
                      section: "life_milestone_2",
                      component: "block_cta_link",
                      label: block_cta_label?.trim() || "cta",
                      step: "1",
                      entity: "life_milestone",
                      parentEntity: "life_milestone_2",
                    })
                  }
                >
                  {block_cta_label}
                  <ArrowDown className="size-4 shrink-0 min-[1024px]:hidden" aria-hidden />
                  <ArrowUpRight
                    className="hidden size-4 shrink-0 min-[1024px]:block"
                    aria-hidden
                  />
                </a>
              ) : (
                <Link
                  href={ctaHrefForLink}
                  className={ctaButtonClass}
                  onClick={() =>
                    ctaClick({
                      section: "life_milestone_2",
                      component: "block_cta_link",
                      label: block_cta_label?.trim() || "cta",
                      step: "1",
                      entity: "life_milestone",
                      parentEntity: "life_milestone_2",
                    })
                  }
                >
                  {block_cta_label}
                  <ArrowDown className="size-4 shrink-0 min-[1024px]:hidden" aria-hidden />
                  <ArrowUpRight
                    className="hidden size-4 shrink-0 min-[1024px]:block"
                    aria-hidden
                  />
                </Link>
              )}
            </div>
          ) : null}
          {block_feature_label ? (
            <div className="flex items-center justify-center gap-3 pt-2 text-[18px] font-bold leading-tight text-white min-[1024px]:justify-start">
              <span
                className="inline-flex size-6 shrink-0 items-center justify-center rounded-[4px] bg-white"
                aria-hidden
              >
                <Check
                  className="size-3.5"
                  strokeWidth={2.5}
                  style={{ color: background_color }}
                />
              </span>
              <span>{block_feature_label}</span>
            </div>
          ) : null}
        </div>

        {/* Hero right — reduced & centered on mobile, 668.4 × 602 on desktop */}
        <div
          className={cn(
            "relative shrink-0 overflow-hidden rounded-[16px] min-[1024px]:rounded-[24px]",
            // mobile: centered, reduced size
            "mx-auto w-full max-w-[360px] min-[640px]:max-w-[440px]",
            // desktop: design size
            "min-[1024px]:mx-0 min-[1024px]:w-[668.4px] min-[1024px]:max-w-full",
            // no image: keep an empty balancing frame on desktop only
            !hero_image && "hidden min-h-[200px] min-[1024px]:block",
          )}
          style={{
            aspectRatio: `${HERO_W} / ${HERO_H}`,
            maxHeight: `${HERO_H}px`,
            backgroundColor: background_color,
          }}
        >
          {hero_image ? (
            <Image
              src={hero_image}
              alt={hero_image_alt || "Section image"}
              fill
              className="object-cover"
              sizes={`(max-width: 1023px) 440px, ${Math.round(HERO_W)}px`}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default LifeMilestone2;
